import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button, { ButtonSizes, ButtonStyles } from '../../Button/Button';
import PropTypes from 'prop-types';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../../ui-components/Typography/Typography';
import { TRANSLATIONS } from '../../../../translations';
import BetaIcon, { IconColors, IconSizes } from '../../Icons/Icon';

const ToggleTruncate = ({ text, rows = 3, buttonStyle = 's', classNames }) => {
  const textRef = useRef(null);

  const [isTruncated, setIsTruncated] = useState(false);
  const [showText, setShowText] = useState(false);
  const [buttonLabel, setButtonLabel] = useState(TRANSLATIONS.SHOW_MORE_TEXT);
  const CHEVRON_DOWN = 'chevronDown';
  const CHEVRON_UP = 'chevronUp';
  const [buttonChevron, setButtonChevron] = useState(CHEVRON_DOWN);

  const buttonLabelVariant =
    buttonStyle !== ToggleTruncateButtonStyles.xs
      ? TypographyVariants.p3
      : TypographyVariants.p4;
  const buttonLabelWeight =
    buttonStyle !== ToggleTruncateButtonStyles.xs
      ? TypographyWeights['semi-bold']
      : TypographyWeights['regular'];
  const buttonIconSize =
    buttonStyle !== ToggleTruncateButtonStyles.xs ? 16 : 12;

  const checkTruncation = useCallback(() => {
    if (textRef.current) {
      const lineHeight = parseFloat(
        getComputedStyle(textRef.current).lineHeight
      );
      const maxHeight = lineHeight * rows;
      const actualHeight = textRef.current.scrollHeight;

      setIsTruncated(actualHeight > maxHeight);
    }
  }, [rows]);

  useEffect(() => {
    checkTruncation();
  }, []);

  const showFullText = () => {
    setShowText(!showText);
    setButtonLabel(
      showText ? TRANSLATIONS.SHOW_MORE_TEXT : TRANSLATIONS.SHOW_LESS
    );
    setButtonChevron(showText ? CHEVRON_DOWN : CHEVRON_UP);
  };

  return (
    <div className={`toggle-truncate ${classNames ? classNames : ''}`}>
      <div
        ref={textRef}
        className={`toggle-truncate__text${
          isTruncated && !showText
            ? `--truncated
            toggle-truncate__text--truncated--${rows}`
            : '--whole'
        }`}
      >
        {text}
      </div>

      {isTruncated && (
        <Button
          style={ButtonStyles.showMoreToggle}
          size={ButtonSizes[buttonStyle]}
          iconRight={
            <BetaIcon
              name={buttonChevron}
              color={IconColors.regular}
              size={IconSizes[buttonIconSize]}
            />
          }
          label={
            <Typography
              variant={buttonLabelVariant}
              weight={TypographyWeights['regular']}
            >
              {buttonLabel}
            </Typography>
          }
          onClick={() => showFullText()}
        />
      )}
    </div>
  );
};

export const ToggleTruncateButtonStyles = {
  xs: 'xs',
  s: 's',
};

ToggleTruncate.propTypes = {
  text: PropTypes.string.isRequired,
  rows: PropTypes.number,
  buttonStyle: PropTypes.oneOf(Object.values(ToggleTruncateButtonStyles)),
};

ToggleTruncate.defaultProps = {
  text: '',
  rows: 3,
  buttonStyle: ToggleTruncateButtonStyles.xs,
};

export { ToggleTruncate };
